import React, { useEffect, useRef, useState } from 'react';

import APIconButton from '@shared/components/atoms/icon-button';
import { useTranslate } from '@shared/hooks/use-translate';
import { deregisterToastEmitter, registerToastEmitter } from '@shared/utils/toast-bus';

import type { EventSubscription } from '@app-types/events';
import type { ToastMessage } from '@shared/utils/toast-bus';

import './styles.scss';

const DEFAULT_TTL = 5000;
const MAX_TOASTS = 5;
const ANIM_MS = 260;

type LocalToast = ToastMessage & { exiting?: boolean };

export const APToast: React.FC = () => {
  const translate = useTranslate();
  const [toasts, setToasts] = useState<LocalToast[]>([]);
  const timers = useRef(
    new Map<string, { timeoutId: number; expiresAt: number; remaining?: number }>()
  );

  useEffect(() => {
    const sub: EventSubscription | void = registerToastEmitter((t) => {
      setToasts((prev) => {
        // newest on top
        const next = [t, ...prev].slice(0, MAX_TOASTS);
        return next;
      });

      const ttl = t.ttl ?? DEFAULT_TTL;
      if (ttl > 0) startTimer(t.id, ttl);
    });
    const timersMap = timers.current;
    return () => {
      deregisterToastEmitter();
      // clear timers
      timersMap.forEach((v) => clearTimeout(v.timeoutId));
      timersMap.clear();
      // unsubscribe if register returned something
      try {
        if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTimer = (id: string, ttl: number) => {
    const expiresAt = Date.now() + ttl;
    const timeoutId = window.setTimeout(() => removeWithAnimation(id), ttl);
    timers.current.set(id, { timeoutId, expiresAt });
  };

  const pauseTimer = (id: string) => {
    const entry = timers.current.get(id);
    if (!entry) return;
    const remaining = Math.max(0, entry.expiresAt - Date.now());
    clearTimeout(entry.timeoutId);
    timers.current.set(id, { timeoutId: -1, expiresAt: Date.now() + remaining, remaining });
  };

  const resumeTimer = (id: string) => {
    const entry = timers.current.get(id);
    if (!entry) return;
    const remaining = entry.remaining ?? Math.max(0, entry.expiresAt - Date.now());
    if (remaining <= 0) return removeWithAnimation(id);
    const timeoutId = window.setTimeout(() => removeWithAnimation(id), remaining);
    timers.current.set(id, { timeoutId, expiresAt: Date.now() + remaining });
  };

  const removeWithAnimation = (id: string) => {
    // mark exiting
    setToasts((s) => s.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    // wait for animation to finish
    setTimeout(() => {
      setToasts((s) => s.filter((t) => t.id !== id));
      const entry = timers.current.get(id);
      if (entry) {
        clearTimeout(entry.timeoutId);
        timers.current.delete(id);
      }
    }, ANIM_MS);
  };

  const dismiss = (id: string) => removeWithAnimation(id);

  const renderToastContent = (t: LocalToast) => (
    <div
      key={t.id}
      className={`ap-toast ap-toast-${t.type ?? 'info'} ${t.exiting ? 'ap-toast--exit' : 'ap-toast--enter'}`}
      onMouseEnter={() => pauseTimer(t.id)}
      onMouseLeave={() => resumeTimer(t.id)}
      onFocus={() => pauseTimer(t.id)}
      onBlur={() => resumeTimer(t.id)}
      tabIndex={0}
    >
      <div className="ap-toast-body">
        <div className="ap-toast-title">{t.title}</div>
        {t.message ? <div className="ap-toast-message">{t.message}</div> : null}
      </div>
      <div className="ap-toast-actions">
        {t.type === 'error' && t.message && (
          <APIconButton
            className="ap-toast-copy"
            icon="copy_line"
            aria-label={translate('aria.copy', undefined, 'Copy')}
            onClick={() => {
              const text = `${t.title}\n${t.message ?? ''}`;
              navigator.clipboard.writeText(text).catch(() => { /* ignore */ });
            }}
            size="small"
            variant="secondary"
          />
        )}
        <APIconButton
          className="ap-toast-close"
          icon="close"
          aria-label={translate('aria.close')}
          onClick={() => dismiss(t.id)}
          size="small"
          variant="secondary"
        />
      </div>
    </div>
  );

  const centerToasts = toasts.filter((t) => t.position === 'center');
  const cornerToasts = toasts.filter((t) => t.position !== 'center');

  return (
    <>
      <div className="ap-toast-container" aria-live="polite" role="status">
        {cornerToasts.map(renderToastContent)}
      </div>
      {centerToasts.length > 0 && (
        <div
          className="ap-toast-backdrop"
          onClick={() => centerToasts.forEach((t) => dismiss(t.id))}
        >
          <div
            className="ap-toast-container--center"
            aria-live="polite"
            role="status"
            onClick={(e) => e.stopPropagation()}
          >
            {centerToasts.map(renderToastContent)}
          </div>
        </div>
      )}
    </>
  );
};

export default APToast;
